import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    ArrowLeftIcon,
    LightningBoltIcon,
    WifiIcon,
    LocationMarkerIcon,
    ClockIcon,
    DeviceMobileIcon,
    ChipIcon,
    RefreshIcon,
    TerminalIcon,
    DatabaseIcon,
    InformationCircleIcon,
    StatusOnlineIcon,
} from '@heroicons/react/outline';
import { useHttpClient } from '../../../shared/http-client';

interface CommandResult {
    status: string;
    response?: string;
    detail?: string;
}

export const DeviceDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation() as { t: (key: string) => string };
    const { api, state } = useHttpClient();

    const [device, setDevice] = useState<any>(null);
    const [connected, setConnected] = useState<boolean | null>(null);
    const [telemetry, setTelemetry] = useState<any>(null);
    const [commandResult, setCommandResult] = useState<CommandResult | null>(null);
    const [loadingCommand, setLoadingCommand] = useState<string | null>(null);
    const pendingCommand = useRef<string | null>(null);

    useEffect(() => {
        if (id) {
            api(`/fleet/tracker-devices/${id}/`, 'GET');
        }
    }, [id]);

    useEffect(() => {
        if (state.error && state.path?.includes('command') && state.method === 'POST') {
            toast.error(t('common.label.command-error'));
            setCommandResult({ status: 'error', detail: String(state.error) });
            setLoadingCommand(null);
            pendingCommand.current = null;
            return;
        }
        if (!state.data || !state.path) return;

        if (state.path.includes('connection_status') && state.method === 'GET') {
            setConnected(state.data.connected);
        } else if (state.path.includes('command') && state.method === 'POST') {
            const data = state.data;
            setCommandResult(data);
            if (data.status === 'ack') {
                toast.success(t('common.label.command-sent-ok'));
            } else if (data.status === 'timeout') {
                toast.warning(t('common.label.command-timeout'));
            } else {
                toast.error(data.detail || t('common.label.command-error'));
            }
            setLoadingCommand(null);
            pendingCommand.current = null;
        } else if (state.path.includes('latest_telemetry') && state.method === 'GET') {
            setTelemetry(state.data);
        } else if (state.path.includes('tracker-devices') && state.method === 'GET') {
            setDevice(state.data);
        }
    }, [state]);

    const checkConnection = () => {
        if (id) {
            api(`/fleet/tracker-devices/${id}/connection_status/`, 'GET');
        }
    };

    const fetchTelemetry = () => {
        if (id) {
            api(`/fleet/tracker-devices/${id}/latest_telemetry/`, 'GET');
        }
    };

    useEffect(() => {
        if (device) {
            checkConnection();
            fetchTelemetry();
        }
    }, [device]);

    const sendCommand = (command: string) => {
        if (!id) return;
        setLoadingCommand(command);
        setCommandResult(null);
        pendingCommand.current = command;
        api(`/fleet/tracker-devices/${id}/command/`, 'POST', { command, timeout: 10.0 });
    };

    const commands = [
        {
            key: 'getver',
            label: t('common.label.cmd-getver'),
            desc: t('common.label.cmd-getver-desc'),
            icon: <InformationCircleIcon className="h-5 w-5" />,
        },
        {
            key: 'getinfo',
            label: t('common.label.cmd-getinfo'),
            desc: t('common.label.cmd-getinfo-desc'),
            icon: <ChipIcon className="h-5 w-5" />,
        },
        {
            key: 'getstatus',
            label: t('common.label.cmd-getstatus'),
            desc: t('common.label.cmd-getstatus-desc'),
            icon: <WifiIcon className="h-5 w-5" />,
        },
        {
            key: 'getio',
            label: t('common.label.cmd-getio'),
            desc: t('common.label.cmd-getio-desc'),
            icon: <DatabaseIcon className="h-5 w-5" />,
        },
        {
            key: 'getgps',
            label: t('common.label.cmd-getgps'),
            desc: t('common.label.cmd-getgps-desc'),
            icon: <LocationMarkerIcon className="h-5 w-5" />,
        },
        {
            key: 'battery',
            label: t('common.label.cmd-battery'),
            desc: t('common.label.cmd-battery-desc'),
            icon: <LightningBoltIcon className="h-5 w-5" />,
        },
    ];

    const ioParams = [
        { id: 67, label: t('common.label.battery-voltage'), icon: <LightningBoltIcon className="h-5 w-5 text-yellow-600" /> },
        { id: 239, label: t('common.label.ignition'), icon: <StatusOnlineIcon className="h-5 w-5 text-orange-600" /> },
        { id: 21, label: t('common.label.gsm-signal'), icon: <WifiIcon className="h-5 w-5 text-blue-600" /> },
        { id: 'satellites', label: t('common.label.gps-satellites'), icon: <LocationMarkerIcon className="h-5 w-5 text-green-600" /> },
    ];

    if (state.isLoading && !device) {
        return (
            <div className="flex justify-center items-center h-64">
                <RefreshIcon className="h-8 w-8 text-gray-400 animate-spin" />
            </div>
        );
    }

    if (!device) {
        return (
            <div className="px-4 py-6">
                <p className="text-gray-500">{t('common.message.no-data')}</p>
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-6 md:px-8 py-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/devices')}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <ArrowLeftIcon className="h-5 w-5 text-gray-700" />
                    </button>
                    <h1 className="text-xl font-bold text-gray-900">
                        {t('common.label.device-detail')} — {device.IMEI}
                    </h1>
                </div>
                <button
                    onClick={() => { checkConnection(); fetchTelemetry(); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                    <RefreshIcon className="h-4 w-4" />
                    {t('common.label.refresh')}
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: Device Info */}
                <div className="lg:col-span-1 space-y-4">
                    {/* Connection status card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <DeviceMobileIcon className="h-5 w-5 text-gray-700" />
                            <h2 className="text-sm font-semibold text-gray-900">{t('common.label.connection-status')}</h2>
                        </div>
                        <div className="flex items-center gap-3">
                            <span
                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                                    connected === true
                                        ? 'bg-green-100 text-green-800'
                                        : connected === false
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-gray-100 text-gray-600'
                                }`}
                            >
                                <span
                                    className={`w-2 h-2 rounded-full ${
                                        connected === true
                                            ? 'bg-green-500'
                                            : connected === false
                                            ? 'bg-red-500'
                                            : 'bg-gray-400'
                                    }`}
                                />
                                {connected === true
                                    ? t('common.label.connected')
                                    : connected === false
                                    ? t('common.label.disconnected')
                                    : t('common.label.unknown')}
                            </span>
                        </div>
                    </div>

                    {/* Device info card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('common.label.device-info')}</h2>
                        <dl className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <dt className="text-gray-500">IMEI</dt>
                                <dd className="font-medium text-gray-900">{device.IMEI}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-gray-500">{t('common.form-label.phone')}</dt>
                                <dd className="font-medium text-gray-900">{device.phone_number || '—'}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-gray-500">{t('common.form-label.matricula-vehiculo')}</dt>
                                <dd className="font-medium text-gray-900">{device.vehicle?.plate || '—'}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-gray-500">ID</dt>
                                <dd className="font-medium text-gray-900">{device.id}</dd>
                            </div>
                        </dl>
                    </div>

                    {/* IO Parameters card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('common.label.io-parameters')}</h2>
                        <p className="text-xs text-gray-400 mb-3">
                            {t('common.label.io-parameters-hint')}
                        </p>
                        <div className="space-y-3">
                            {ioParams.map((param) => {
                                const ioVal = connected
                                    ? (param.id === 'satellites'
                                        ? telemetry?.satellites
                                        : telemetry?.io_elements?.[String(param.id)]?.value)
                                    : undefined;
                                const displayVal = ioVal !== undefined && ioVal !== null
                                    ? (param.id === 239
                                        ? (ioVal ? t('common.label.yes') : t('common.label.no'))
                                        : String(ioVal))
                                    : t('common.label.na');
                                return (
                                    <div key={param.id} className="flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-2">
                                            {param.icon}
                                            <span className="text-gray-700">{param.label}</span>
                                        </div>
                                        <span className="font-medium text-gray-900">
                                            {displayVal}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-xs text-gray-400 mt-3 italic">
                            {t('common.label.io-parameters-note')}
                        </p>
                    </div>
                </div>

                {/* Right: Commands */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <TerminalIcon className="h-5 w-5 text-gray-700" />
                            <h2 className="text-sm font-semibold text-gray-900">{t('common.label.gprs-commands')}</h2>
                        </div>

                        {connected === false && (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                                {t('common.label.device-offline-warning')}
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {commands.map((cmd) => (
                                <button
                                    key={cmd.key}
                                    onClick={() => sendCommand(cmd.key)}
                                    disabled={loadingCommand !== null || connected === false}
                                    className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="flex-shrink-0 mt-0.5 text-gray-600">
                                        {cmd.icon}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900">
                                            {cmd.label}
                                            {loadingCommand === cmd.key && (
                                                <RefreshIcon className="inline-block ml-2 h-4 w-4 animate-spin text-blue-500" />
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {cmd.desc}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Command result */}
                    {commandResult && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                            <div className="flex items-center gap-2 mb-3">
                                <ClockIcon className="h-5 w-5 text-gray-700" />
                                <h2 className="text-sm font-semibold text-gray-900">{t('common.label.command-response')}</h2>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">{t('common.label.status')}:</span>
                                    <span
                                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                                            commandResult.status === 'ack'
                                                ? 'bg-green-100 text-green-800'
                                                : commandResult.status === 'nack'
                                                ? 'bg-red-100 text-red-800'
                                                : 'bg-gray-100 text-gray-700'
                                        }`}
                                    >
                                        {commandResult.status.toUpperCase()}
                                    </span>
                                </div>
                                {commandResult.response && (
                                    <div>
                                        <span className="text-gray-500">{t('common.label.response')}:</span>
                                        <pre className="mt-1 p-3 bg-gray-50 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all">
                                            {commandResult.response}
                                        </pre>
                                    </div>
                                )}
                                {commandResult.detail && (
                                    <div className="text-xs text-gray-500">
                                        {commandResult.detail}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
